// src/components/navigation/Breadcrumbs.tsx
import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../utils/i18nStub";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";

// Export so pages can type custom crumbs when needed
export type Crumb = { label: string; to?: string | null; state?: any };

interface BreadcrumbsProps {
  items?: Crumb[];             // optional manual trail
  separator?: React.ReactNode; // default "/"
  className?: string;          // optional extra classes on <nav>
}

/** helper */
const c = (label: string, to?: string | null, state?: any): Crumb => ({ label, to, state });

// Route regexes
const rx = {
  landDetail: /^\/benefaction\/lands\/(\d+)$/,
  landList: /^\/benefaction\/lands$/,
  applyRequest: /^\/benefaction\/request\/(\d+)$/,
  requestsList: /^\/benefaction\/requests$/,
  requestDetail: /^\/benefaction\/requests\/(\d+)$/,
  donorsList: /^\/benefaction\/donors$/,
  donorDetail: /^\/benefaction\/donors\/(\d+)$/,
  benefactorList: /^\/benefaction\/BenefactorList$/,
  mosquesList: /^\/mosques$/,
  mosqueDetail: /^\/mosques\/(\d+)$/,
  mosqueSearchLegacy1: /^\/mosque\/mosque-search$/,
  mosqueSearchLegacy2: /^\/mosqque\/mosque-search$/,
  services: /^\/services$/,
  mosqueSearchService: /^\/service\/mosque-search$/,
  availableLandsService: /^\/service\/available-lands$/,
  mosqueContributionService: /^\/service\/mosque-contribution$/,
  mosqueSearch: /^\/mosquesearch$/,
};

export default function Breadcrumbs({ items, separator, className }: BreadcrumbsProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const sep =
    typeof separator !== "undefined" ? (
      separator
    ) : (
      <HugeiconsIcon icon={ArrowLeft01Icon} size={18} color="#417505" strokeWidth={1.5} />
    );

  // Centralized labels (i18n-safe fallbacks)
  const L = React.useMemo(
    () => ({
      home: t("breadcrumb.home", "الرئيسية"),
      benefaction: t("breadcrumb.benefaction", "الفرص المتاحة لبناء مسجد"),
      land: t("breadcrumb.land", "الأرض"),
      apply: t("breadcrumb.apply", "تقديم الطلب"),
      mosquesSection: t("breadcrumb.mosquesSection", "بحث المساجد"),
      mosque: t("breadcrumb.mosque", "المسجد"),
      donors: t("breadcrumb.donors", "المتبرعون"),
      donorProfile: t("breadcrumb.donorProfile", "ملف المتبرع"),
      benefactorList: t("breadcrumb.benefactorList", "قائمة المساهمين"),
      requests: t("breadcrumb.requests", "طلبات الإحسان"),
      request: t("breadcrumb.request", "الطلب"),
      services: t("breadcrumb.services", "الخدمات"),
      mosqueSearchService: t("breadcrumb.mosqueSearchService", "خدمة البحث عن المساجد"),
      availableLandsService: t("breadcrumb.availableLandsService", "خدمة البحث عن الأراضي المتاحة لبناء مسجد"),
      mosqueContributionService: t("breadcrumb.mosqueContributionService", "خدمة فرص المساهمة في النظافة والصيانة"),
    }),
    [t]
  );

  const path = React.useMemo(() => pathname.replace(/\/+$/, ""), [pathname]);

  // Auto-build crumbs unless items are provided manually
  const autoItems: Crumb[] = React.useMemo(() => {
    const ruleBook: Array<(p: string) => Crumb[] | null> = [
      // Home
      (p) => (p === "" || p === "/" ? [c(L.home, null)] : null),

      // Lands list
      (p) => (rx.landList.test(p) ? [c(L.home, "/"), c(L.benefaction, null)] : null),

      // Land detail: /benefaction/lands/:id
      (p) => {
        const m = p.match(rx.landDetail);
        if (!m) return null;
        return [c(L.home, "/"), c(L.benefaction, "/benefaction/lands"), c(L.land, null)];
      },

      // APPLY REQUEST: /benefaction/request/:id
      // Do NOT include BenefactorList here. It's a separate page that the user
      // visits only when they explicitly choose to add/select a benefactor.
      (p) => {
        const m = p.match(rx.applyRequest);
        if (!m) return null;
        return [
          c(L.home, "/"),
          c(L.benefaction, "/benefaction/lands"),
          c(L.apply, null),
        ];
      },

      // Requests (list + detail)
      (p) => (rx.requestsList.test(p) ? [c(L.home, "/"), c(L.requests, null)] : null),
      (p) => {
        const m = p.match(rx.requestDetail);
        if (!m) return null;
        return [c(L.home, "/"), c(L.requests, "/benefaction/requests"), c(L.request, null)];
      },

      // Donors (list + profile)
      (p) => (rx.donorsList.test(p) ? [c(L.home, "/"), c(L.donors, null)] : null),
      (p) => {
        const m = p.match(rx.donorDetail);
        if (!m) return null;
        return [c(L.home, "/"), c(L.donors, "/benefaction/donors"), c(L.donorProfile, null)];
      },

      // BenefactorList: /benefaction/BenefactorList
      (p) =>
        rx.benefactorList.test(p)
          ? [c(L.home, "/"), c(L.benefaction, "/benefaction/lands"), c(L.benefactorList, null)]
          : null,

      // Mosques (list + detail)
      (p) => (rx.mosquesList.test(p) ? [c(L.home, "/"), c(L.mosquesSection, null)] : null),
      (p) => {
        const m = p.match(rx.mosqueDetail);
        if (!m) return null;
        return [c(L.home, "/"), c(L.mosquesSection, "/mosques"), c(L.mosque, null)];
      },

      // Legacy mosque search → treat as list
      (p) =>
        rx.mosqueSearchLegacy1.test(p) || rx.mosqueSearchLegacy2.test(p)
          ? [c(L.home, "/"), c(L.mosquesSection, null)]
          : null,

      // Services pages
      (p) => (rx.services.test(p) ? [c(L.home, "/"), c(L.services, null)] : null),
      (p) =>
        rx.mosqueSearchService.test(p)
          ? [c(L.home, "/"), c(L.services, "/services"), c(L.mosqueSearchService, null)]
          : null,
      (p) =>
        rx.availableLandsService.test(p)
          ? [c(L.home, "/"), c(L.services, "/services"), c(L.availableLandsService, null)]
          : null,
      (p) =>
        rx.mosqueContributionService.test(p)
          ? [c(L.home, "/"), c(L.services, "/services"), c(L.mosqueContributionService, null)]
          : null,

      // Mosque search page (/mosquesearch)
      (p) => (rx.mosqueSearch.test(p) ? [c(L.home, "/"), c(L.mosquesSection, null)] : null),
    ];

    if (items?.length) return items;
    for (const rule of ruleBook) {
      const out = rule(path);
      if (out) return out;
    }
    return [c(L.home, "/")];
  }, [items, path, L]);

  return (
    <nav aria-label="breadcrumb" className={`mb-4 ${typeof className === "string" ? className : ""}`}>
      <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
        {autoItems.map((b, i) => {
          const isLast = i === autoItems.length - 1;
          const content = b.to && !isLast ? (
            <Link
              to={b.to}
              state={b.state}
              className="hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors cursor-pointer underline-offset-2 hover:underline"
            >
              {b.label}
            </Link>
          ) : (
            <span
              aria-current={isLast ? "page" : undefined}
              className={isLast ? "text-neutral-800 dark:text-neutral-100" : ""}
            >
              {b.label}
            </span>
          );
          return (
            <li key={`${b.label}-${i}`} className="flex items-center gap-2">
              {content}
              {!isLast && <span className="opacity-60">{sep}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
